import React, { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import type { InvitationValidationReason } from '@bingads-webui-campaign-react/accept-invitation-api';
import {
  mergeClasses,
  Text,
  Title1,
  tokens,
} from '@fluentui/react-components';
import {
  ClockRegular,
  ErrorCircleRegular,
  PeopleTeamRegular,
  PresenceBlockedRegular,
} from '@fluentui/react-icons';

import { useUserInvitationSelectionPageStyles } from './use-user-invitation-selection-page-styles';

// Narrows the server's `reason` (from a `Validate` 400 `InvitationValidationFailed`)
// to a known value, defaulting any unrecognized reason to `Unknown` (generic error)
// so the page stays forward-compatible with future CCMT classifications.
export const normalizeValidationReason = (reason?: string): InvitationValidationReason => {
  switch (reason) {
    case 'Expired':
    case 'Invalid':
    case 'UsersLimitReached':
    case 'Inactive':
      return reason;
    default:
      return 'Unknown';
  }
};

export type InvitationErrorViewProps = {
  reason: InvitationValidationReason;
};

// Reason-specific error page (no action buttons — the "Get help here" footer is the
// only affordance). Reuses the existing left-column layout (danger badge + title + copy).
export const InvitationErrorView: React.FC<InvitationErrorViewProps> = ({ reason }) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useUserInvitationSelectionPageStyles();

  let badgeLabel;
  let badgeIcon;
  let errorTitle;
  let errorDescription;

  switch (reason) {
    case 'Expired':
      badgeLabel = _TL_('Invitation expired');
      badgeIcon = <ClockRegular className={classes.invitationPillIcon} aria-hidden />;
      errorTitle = _TL_('This invitation has expired');
      errorDescription = _TL_('This invitation link is no longer valid because it has expired. Invitation links are valid for 30 days from the date they\'re sent. To get access, ask the person who invited you to send a new invitation.');
      break;
    case 'UsersLimitReached':
      badgeLabel = _TL_('User limit reached');
      badgeIcon = <PeopleTeamRegular className={classes.invitationPillIcon} aria-hidden />;
      errorTitle = _TL_('This account has reached its user limit');
      errorDescription = _TL_('This account already has the maximum number of users, so you can\'t be added right now. Contact the account admin for help.');
      break;
    case 'Inactive':
      badgeLabel = _TL_('Ads account inactive');
      badgeIcon = <PresenceBlockedRegular className={classes.invitationPillIcon} aria-hidden />;
      errorTitle = _TL_('This ad account is inactive');
      errorDescription = _TL_('Reach out to the person who invited you for next steps or contact support.');
      break;
    // Any unrecognized reason renders the same "invitation unavailable" page as Invalid.
    case 'Invalid':
    default:
      badgeLabel = _TL_('Invitation unavailable');
      badgeIcon = <ErrorCircleRegular className={classes.invitationPillIcon} aria-hidden />;
      errorTitle = _TL_('This link no longer works');
      errorDescription = _TL_('You may have already accepted this invitation, or it has been canceled. Reach out to the person who invited you.');
      break;
  }

  return (
    <div className={classes.textAndButtons}>
      <div className={classes.invitationMeta}>
        <Text
          className={mergeClasses('invitation-error-pill', classes.invitationPill, classes.invitationPillDanger)}
          weight="semibold"
        >
          {badgeIcon}
          {i18n.getString(badgeLabel)}
        </Text>
      </div>
      <Title1 as="h1" block className={mergeClasses('idp-title', 'invitation-error-title', classes.title)}>
        {i18n.getString(errorTitle)}
      </Title1>
      <Text block size={300} color={tokens.colorNeutralForeground2}>
        {i18n.getString(errorDescription)}
      </Text>
    </div>
  );
};
